import { defineComponent, computed } from "vue";
import { Container } from "aurelia-dependency-injection";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";

import { AssetsService } from "../../../../../services/assets-service";
import { ToastrService } from "../../../../../services/toastr.service";
import { IGetCompletedPatientChart, PatientService } from "../../../../../services/patient.service";
import {
    ApplicationEnum,
    DocumentTypeCategory,
    ParentPermissionEnum,
    PermissionActionEnum
} from "../../../../../enums/enums";
import { IGetDocumentTypeLean } from "../../../../../resources-vue/vue-interfaces/i-document-type";
import { ReferralService } from "../../../../../services/referral-service";
import { DownloadService } from "../../../../../services/download.service";
import { IPromptOptions } from "../../../../../resources-vue/vue-dialogs/prompt/prompt";
import { formatDate } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { IGetHHReferralDocumentsResponse } from "../../../../../resources-vue/vue-interfaces/i-get-referral";
import { ICompletedPatientChartsResponse } from "../../../../../interfaces/i-patient-service";
import { IDownloadAsset } from "../../../../../resources-vue/vue-interfaces/i-asset";

import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import TableEmpty from "../../../../../resources-vue/vue-custom-elements/TableEmpty/TableEmpty.vue";
import EditHHDocument from "../../../../../resources-vue/vue-dialogs/EditHHDocument/EditHHDocument.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import { IDownloadCharts } from "../../../../../resources-vue/vue-interfaces/i-patient";

export default defineComponent({
    components: {
        TableEmpty,
        TypeaheadInput
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    props: {
        title: { type: String },
        referral: { type: Object },
        uploadDocument: { type: Function },
        getDocuments: { type: Function },
        editDocument: { type: Function },
        deleteDocument: { type: Function },
        getPatientCharts: { type: Object }
    },

    validations() {
        return {
            fileDetails: this.fileDetails.map((detail) => ({
                uploadTypeId: { required }
            }))
        };
    },

    data() {
        return {
            fileDetails: [],
            uploading: false,
            _assetsService: null,
            _toastrService: null,
            _patientService: null,
            _assetService: null,
            _downloadService: null,
            loadingDocuments: false,
            patientDocuments: [],
            patientCharts: [],
            addPatientDocumentsPermission: false,
            editPatientDocumentsPermission: false,
            deletePatientDocumentsPermission: false,
            searchedTypes: [],
            DocumentTypeCategoryEnum: DocumentTypeCategory,
            _referralService: null,
            _permissionManager: null,
            loadingCount: 5,
            dialogRef: null,
            pdfRes: null,
            selectedUploadType: undefined,
            v$: useVuelidate(),
            formatDate
        };
    },

    created() {
        this._assetsService = Container.instance.get(AssetsService);
        this._toastrService = Container.instance.get(ToastrService);
        this._referralService = Container.instance.get(ReferralService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._patientService = Container.instance.get(PatientService);
        this._downloadService = Container.instance.get(DownloadService);
    },

    async mounted() {
        await this.loadSearchedTypes();
        this.addPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canAdd,
            this.referral.accountId
        );
        this.deletePatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canDelete,
            this.referral.accountId
        );
        this.editPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canEdit,
            this.referral.accountId
        );
        await this.loadFilesList();
    },

    methods: {
        async loadSearchedTypes() {
            if (!this.referral.patientId) {
                this.searchedTypes = [];
            }
            let category = Number(this.DocumentTypeCategoryEnum.enum.Patient);
            this.loadingDocuments = true;
            let searchedTypes: IGetDocumentTypeLean[] = await this._referralService.getDocumentTypesList({
                providerIds: [this.referral.providerId],
                application: this.referral.application,
                accountId: this.referral.accountId,
                category
            });
            this.loadingDocuments = false;
            if (searchedTypes) {
                this.searchedTypes = searchedTypes.map((documentType: IGetDocumentTypeLean) => ({
                    value: documentType.id,
                    name: documentType.type
                }));
            } else {
                this.searchedTypes = [];
            }
        },

        updateTypeId(index: any, id: string) {
            this.fileDetails[index].uploadTypeId = id;
        },

        removeFile(index: number) {
            this.fileDetails.splice(index, 1);
        },

        removeAllFiles() {
            this.fileDetails = [];
        },

        uploadFilesChanged(event: any) {
            this.fileDetails = [...this.fileDetails, { file: event.target.files[0], uploadTypeId: null }];
        },

        async editSelectedDocument(document: IGetHHReferralDocumentsResponse) {
            this.dialogRef = this.$dialog.open(EditHHDocument, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false,
                    style: "width: 35vw"
                },
                data: {
                    document: document,
                    referralId: this.referral.id,
                    application: this.referral.application,
                    agencyId: this.referral.accountId,
                    searchedTypes: this.searchedTypes
                },
                onClose: async (options) => {
                    if (options.data == "success") {
                        this.loadFilesList();
                        this._toastrService.success({
                            message: `The document has been successfully Edited.`,
                            title: `Document Edited`
                        });
                    } else if (options.data == "error") {
                        this._toastrService.error({
                            title: `Error - Document`,
                            message: `There was a problem while editing the document. Please try again.`
                        });
                    }
                }
            });
        },

        async deleteSelectedDocument(documentId: string) {
            try {
                let promptOptions: IPromptOptions = {
                    message: "Are you sure you want to delete?",
                    okText: "Yes",
                    cancelText: "No"
                };

                this.dialogRef = this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        contentStyle: {
                            overflowX: "hidden"
                        },
                        showHeader: false
                    },
                    data: {
                        promptOptions
                    },
                    onClose: async (confirm) => {
                        if (confirm.data === "success") {
                            const response = await this.deleteDocument(documentId);
                            this.loadFilesList();
                            if (response.isSuccessful) {
                                this._toastrService.success({
                                    message: `The document has been successfully deleted.`,
                                    title: `Document Deleted`
                                });
                            } else {
                                this._toastrService.error({
                                    title: `Error - Document`,
                                    message: `There was a problem while deleting the document. Please try again.`
                                });
                            }
                        }
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Document`,
                    message: `There was a problem while deleting the document. Please try again.`
                });
            }
        },

        async getCompletedPatientChart() {
            const getCompletedPatientChartQuery: IGetCompletedPatientChart = {
                referralId: this.referral.id,
                application: this.referral.application,
                agencyId: this.referral.accountId
            };

            return await this._patientService.getCompletedPatientCharts(getCompletedPatientChartQuery);
        },

        extractAssetDownloadPayload(documents: IDownloadCharts): IDownloadAsset {
            const docId =
                documents.Application == ApplicationEnum.AxxessHospiceFE ||
                documents.Application == ApplicationEnum.AxxessPalliative
                    ? documents.Id
                    : documents.AssetId;
            return {
                assetId: docId,
                application: documents.Application || this.referral.application,
                agencyId: documents.AgencyId
            };
        },

        async downloadDocuments(documents: IDownloadCharts) {
            const downloadDocument = this.extractAssetDownloadPayload(documents);
            await this._assetsService
                .downloadPatientDocuments(downloadDocument)
                .then((response: any) => {
                    this._downloadService.downloadFile(response, response.type);
                    this._toastrService.success({
                        message: `The requested patient/client document is downloaded.`
                    });
                })
                .catch((error: any) => {
                    if (error && documents.Status === "Running") {
                        this._toastrService.info({
                            message: `The requested patient/client document is in-progress, you will be notified when the document is completed.`
                        });
                    } else {
                        this._toastrService.error({
                            title: `Opening Document Failed`,
                            message: `Opening Document Failed, Please Contact Axxess if issue still persists.`
                        });
                    }
                });
        },

        async openSelectedDocument(documents: IDownloadCharts) {
            const downloadDocument = this.extractAssetDownloadPayload(documents);

            return await this._assetsService
                .downloadPatientChart(downloadDocument)
                .then((response: any) => {
                    const url = window.URL.createObjectURL(
                        new Blob([response.body], {
                            type:
                                downloadDocument.application == ApplicationEnum.HomeCare
                                    ? "application/pdf"
                                    : "application/zip"
                        })
                    );
                    window.open(url, "_blank");
                    this._toastrService.success({
                        message: `The requested patient/client chart document is downloaded.`
                    });
                    this.loadingDocuments = false;
                })
                .catch((error: any) => {
                    if (error && documents.Status === "Running") {
                        this._toastrService.info({
                            message: `The requested patient/client chart document is in-progress, you will be notified when the document is completed.`
                        });
                    } else {
                        this._toastrService.error({
                            title: `Opening Document Failed`,
                            message: `Opening Document Failed, Please Contact Axxess if issue still persists.`
                        });
                    }
                });
        },

        getFileIcon(type: string) {
            if (!type) {
                return "fa-file";
            }
            if (type.match("image.*")) {
                return "fa-file-image";
            } else if (type.includes("wordprocessing")) {
                return "fa-file-word";
            } else if (type.includes("spreadsheet")) {
                return "fa-file-excel";
            } else if (type.includes("pdf")) {
                return "fa-file-pdf";
            } else {
                return "fa-file";
            }
        },

        async uploadDocuments() {
            const validated = await this.v$.$validate();
            if (validated) {
                try {
                    this.uploading = true;
                    for (let i = 0; i < this.fileDetails?.length; i++) {
                        const formData = new FormData();
                        formData.append(`attachment${i}`, this.fileDetails[i].file);
                        let payload = {
                            agencyId: this.referral.accountId,
                            application: this.referral.application,
                            referralId: this.referral.id,
                            uploadTypeId: this.fileDetails[i].uploadTypeId,
                            fileName: this.fileDetails[i].file.name
                        };
                        formData.append("request", JSON.stringify(payload));
                        await this.uploadDocument(formData);
                    }
                    this.removeAllFiles();
                    this.loadFilesList();
                    this._toastrService.success({
                        title: `Upload Successful`,
                        message: `The files have been successfully uploaded`
                    });
                } catch (error) {
                    await this.showUploadError();
                }
                this.uploading = false;
            } else {
                this._toastrService.error({
                    title: `Error`,
                    message: `Please fill required Fields`
                });
            }
        },

        async loadFilesList() {
            this.loadingDocuments = true;
            this.patientDocuments = await this.getDocuments();
            const data = await this.getCompletedPatientChart();
            if (data) {
                this.patientCharts = JSON.parse(JSON.stringify(data)).Data;
            }
            this.documentConcatentation(this.patientCharts);
            this.loadingDocuments = false;
        },

        async documentConcatentation(patientChart: Array<object>) {
            const modifiedObjArr = patientChart.map((item: ICompletedPatientChartsResponse) => {
                const assetId = item.AssetId.split("-")[3];
                return {
                    DocumentName: `${this.referral.firstName}_${this.referral.lastName}_${assetId}_PatientChart`,
                    DocumentTypeName: item.Format,
                    ModifiedDateFormatted: item.Completed,
                    IsPatientChart: true,
                    ...item
                };
            });

            if (modifiedObjArr) {
                this.patientDocuments = this.patientDocuments
                    ? modifiedObjArr.concat(this.patientDocuments)
                    : modifiedObjArr;
            }
        },

        async showUploadError() {
            this._toastrService.error({
                title: `Error`,
                message: `There was a problem while uploading files. Please try again.`
            });
        },

        isFieldRequired(index: number) {
            return this.v$.$errors.find((error) =>
                error.$propertyPath === `fileDetails.${index}.uploadTypeId` ? true : false
            );
        }
    }
});
